export interface ContextField {
	id: string;
	label: string;
	type: 'string' | 'number' | 'boolean' | 'object' | 'array';
	description?: string;
}

export interface NodeContext {
	nodeId: string;
	nodeType: string;
	nodeLabel: string;
	fields: ContextField[];
}

// Define what data each node type produces
export const NODE_OUTPUT_DEFINITIONS: Record<string, (nodeData: any) => ContextField[]> = {
	'submission-form': nodeData => {
		try {
			// Extract form fields from the submission form configuration
			const formFields = nodeData.config?.fields || [];
			const fields: ContextField[] = [];

			formFields.forEach((field: any) => {
				// Create a user-friendly field ID based on the label
				const createFieldId = (label: string) => {
					return label
						.toLowerCase()
						.replace(/[^a-z0-9\s]/g, '') // Remove special characters
						.replace(/\s+/g, '_') // Replace spaces with underscores
						.replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
				};

				// Use fieldId if it exists, otherwise create one from the label
				const fieldId = field.fieldId || createFieldId(field.label);

				if (field.type === 'checkbox') {
					// Handle checkbox fields specially - create a field for each option
					if (field.checkboxOptions) {
						field.checkboxOptions.forEach((option: any) => {
							fields.push({
								id: `${fieldId}_${option.value}`,
								label: `${field.label} - ${option.label}`,
								type: 'boolean',
								description: `Checkbox option: ${option.label}`,
							});
						});
					} else if (field.options) {
						// Legacy format
						field.options.forEach((option: string) => {
							fields.push({
								id: `${fieldId}_${option}`,
								label: `${field.label} - ${option}`,
								type: 'boolean',
								description: `Checkbox option: ${option}`,
							});
						});
					}
				} else {
					// Handle other field types
					fields.push({
						id: fieldId,
						label: field.label,
						type: getFieldType(field.type),
						description: `Form field: ${field.label}`,
					});
				}
			});

			return fields;
		} catch (error) {
			console.warn('Error processing submission-form fields:', error);
			return [];
		}
	},

	// Handle reclassified start.form nodes (same as submission-form)
	'start.form': nodeData => {
		try {
			// Extract form fields from the start.form configuration (same logic as submission-form)
			const formFields = nodeData.config?.fields || [];
			const fields: ContextField[] = [];

			formFields.forEach((field: any) => {
				// Create a user-friendly field ID based on the label
				const createFieldId = (label: string) => {
					return label
						.toLowerCase()
						.replace(/[^a-z0-9\s]/g, '') // Remove special characters
						.replace(/\s+/g, '_') // Replace spaces with underscores
						.replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
				};

				// Use fieldId if it exists, otherwise create one from the label
				const fieldId = field.fieldId || createFieldId(field.label);

				if (field.type === 'checkbox') {
					// Handle checkbox fields specially - create a field for each option
					if (field.checkboxOptions) {
						field.checkboxOptions.forEach((option: any) => {
							fields.push({
								id: `${fieldId}_${option.value}`,
								label: `${field.label} - ${option.label}`,
								type: 'boolean',
								description: `Checkbox option: ${option.label}`,
							});
						});
					} else if (field.options) {
						// Legacy format
						field.options.forEach((option: string) => {
							fields.push({
								id: `${fieldId}_${option}`,
								label: `${field.label} - ${option}`,
								type: 'boolean',
								description: `Checkbox option: ${option}`,
							});
						});
					}
				} else {
					// Handle other field types
					fields.push({
						id: fieldId,
						label: field.label,
						type: getFieldType(field.type),
						description: `Form field: ${field.label}`,
					});
				}
			});

			return fields;
		} catch (error) {
			console.warn('Error processing start.form fields:', error);
			return [];
		}
	},

	'start.webhook': nodeData => {
		try {
			const fields: ContextField[] = [];
			const config = nodeData.config || {};

			// Add user-defined response fields
			const responseFields = config.responseFields || [];
			responseFields.forEach((field: any) => {
				fields.push({
					id: field.key,
					label:
						field.key.charAt(0).toUpperCase() +
						field.key.slice(1).replace(/([A-Z])/g, ' $1'),
					type: field.type as 'string' | 'number' | 'boolean' | 'object' | 'array',
					description: `Webhook payload field: ${field.key} (from ${field.path})`,
				});
			});

			// Also include common webhook metadata
			const commonFields = [
				{
					id: 'webhookPayload',
					label: 'Webhook Payload',
					type: 'object' as const,
					description: 'Complete webhook payload data',
				},
				{
					id: 'webhookPath',
					label: 'Webhook Path',
					type: 'string' as const,
					description: 'Webhook endpoint path that was triggered',
				},
				{
					id: 'webhookHeaders',
					label: 'Webhook Headers',
					type: 'object' as const,
					description: 'HTTP headers from the webhook request',
				},
			];

			return [...fields, ...commonFields];
		} catch (error) {
			console.warn('Error processing start.webhook fields:', error);
			return [];
		}
	},

	'start.http-endpoint': nodeData => {
		try {
			const fields: ContextField[] = [];
			const config = nodeData.config || {};

			// Always provide basic input access
			fields.push({
				id: 'input',
				label: 'Request Input',
				type: 'object',
				description: 'Data sent in the HTTP request body',
			});

			// If input schema is configured, add specific fields
			if (config.inputSchema?.type === 'simple' && config.inputSchema.fields) {
				config.inputSchema.fields.forEach((field: any) => {
					if (field.name && field.name.trim()) {
						fields.push({
							id: `input.${field.name}`,
							label: field.name,
							type: field.type || 'string',
							description:
								field.description ||
								`Input field: ${field.name}${
									field.type === 'array'
										? ' (array of values for IN queries)'
										: ''
								}`,
						});
					}
				});
			}

			// Add trigger metadata
			fields.push(
				{
					id: 'triggeredAt',
					label: 'Triggered At',
					type: 'string',
					description: 'ISO timestamp when the HTTP endpoint was called',
				},
				{
					id: 'triggerType',
					label: 'Trigger Type',
					type: 'string',
					description: 'Type of trigger (always "http-endpoint")',
				},
				{
					id: 'correlationId',
					label: 'Correlation ID',
					type: 'string',
					description: 'Request correlation ID for tracking',
				}
			);

			return fields;
		} catch (error) {
			console.warn('Error processing start.http-endpoint fields:', error);
			// Fallback: provide basic input access
			return [
				{
					id: 'input',
					label: 'Request Input',
					type: 'object',
					description: 'Data sent in the HTTP request body',
				},
			];
		}
	},

	'api-call': nodeData => {
		const fields: ContextField[] = [];
		const config = nodeData.config || {};

		// Determine the storage key - use storeAs if provided, otherwise use node ID
		const storageKey = config.storeAs?.trim() || nodeData.id || 'api_response';

		// Add user-defined response fields (new feature)
		const responseFields = config.responseFields || [];
		responseFields.forEach((field: any) => {
			fields.push({
				id: field.key,
				label:
					field.key.charAt(0).toUpperCase() +
					field.key.slice(1).replace(/([A-Z])/g, ' $1'),
				type: field.type as 'string' | 'number' | 'boolean' | 'object' | 'array',
				description: `API response field: ${field.key} (from ${field.path})`,
			});
		});

		// Fallback to response schema if no response fields are defined
		if (responseFields.length === 0) {
			const responseSchema = config.responseSchema || {};
			const schemaFields = Object.entries(responseSchema).map(([key, type]) => ({
				id: key,
				label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
				type: type as 'string' | 'number' | 'boolean' | 'object' | 'array',
				description: `API response field: ${key}`,
			}));
			fields.push(...schemaFields);
		}

		// Also include common response metadata
		const commonFields = [
			{
				id: 'statusCode',
				label: 'Response Status',
				type: 'number' as const,
				description: 'HTTP status code',
			},
			{
				id: 'headers',
				label: 'Response Headers',
				type: 'object' as const,
				description: 'HTTP response headers',
			},
			{
				id: 'data',
				label: 'Response Body',
				type: 'object' as const,
				description: 'Complete API response body',
			},
		];

		return [...fields, ...commonFields];
	},

	transform: nodeData => {
		// Transform nodes produce transformed data based on their transformation configurations
		const config = nodeData.config;
		const fields: ContextField[] = [];

		if (!config || !config.transformations) {
			return [
				{
					id: 'transformed_data',
					label: 'Transformed Data',
					type: 'object',
					description: 'Data after transformation',
				},
			];
		}

		// Create outputs based on transformation output fields
		const transformationFields = config.transformations
			.filter((transformation: any) => transformation.output && transformation.output.trim())
			.map((transformation: any) => ({
				id: transformation.output,
				label:
					transformation.output.charAt(0).toUpperCase() +
					transformation.output.slice(1).replace(/_/g, ' '),
				type: 'string' as const, // Default to string, could be inferred from expression
				description:
					transformation.description || `Transformed field: ${transformation.output}`,
			}));

		fields.push(...transformationFields);

		// Add common transformation metadata fields
		const metadataFields = [
			{
				id: 'transformationMetadata',
				label: 'Transformation Metadata',
				type: 'object' as const,
				description: 'Metadata about transformation execution',
			},
		];

		// If preserveOriginal is enabled, include original data field
		const originalDataFields =
			config.preserveOriginal !== false
				? [
						{
							id: 'originalData',
							label: 'Original Data',
							type: 'object' as const,
							description: 'Original context data before transformation',
						},
				  ]
				: [];

		// If no transformation fields are defined, provide a fallback
		if (transformationFields.length === 0) {
			fields.push({
				id: 'transformed_data',
				label: 'Transformed Data',
				type: 'object',
				description: 'Data after transformation',
			});
		}

		return [...fields, ...metadataFields, ...originalDataFields];
	},

	condition: nodeData => {
		// Condition nodes produce the evaluation result and can route to true/false branches
		const config = nodeData.config;
		const conditionsCount = config?.conditions?.length || 0;

		return [
			{
				id: 'condition_result',
				label: 'Condition Result',
				type: 'boolean',
				description: 'Boolean result of condition evaluation',
			},
			{
				id: 'evaluated_data',
				label: 'Evaluated Data',
				type: 'object',
				description: 'Original data that was evaluated',
			},
			{
				id: 'match_type',
				label: 'Match Type',
				type: 'string',
				description: 'Whether ALL or ANY conditions needed to pass',
			},
			{
				id: 'conditions_count',
				label: 'Conditions Count',
				type: 'number',
				description: `Number of conditions evaluated (${conditionsCount})`,
			},
		];
	},

	database: nodeData => {
		// Database nodes produce standardized query results
		return [
			{
				id: 'rows',
				label: 'Query Rows',
				type: 'array',
				description: 'Array of all result rows from database query',
			},
			{
				id: 'first',
				label: 'First Row',
				type: 'object',
				description: 'First result row (null if no results)',
			},
			{
				id: 'rowCount',
				label: 'Row Count',
				type: 'number',
				description: 'Number of rows returned by query',
			},
		];
	},

	email: nodeData => {
		// Email nodes produce comprehensive email metadata
		return [
			{
				id: 'message_id',
				label: 'Message ID',
				type: 'string',
				description: 'Unique identifier for sent email (e.g., abc-123)',
			},
			{
				id: 'email_sent',
				label: 'Email Sent',
				type: 'boolean',
				description: 'Whether email was sent successfully',
			},
			{
				id: 'to_recipients',
				label: 'To Recipients',
				type: 'array',
				description: 'List of recipient email addresses',
			},
			{
				id: 'cc_recipients',
				label: 'CC Recipients',
				type: 'array',
				description: 'List of CC email addresses',
			},
			{
				id: 'bcc_recipients',
				label: 'BCC Recipients',
				type: 'array',
				description: 'List of BCC email addresses',
			},
			{
				id: 'subject_line',
				label: 'Subject Line',
				type: 'string',
				description: 'Final rendered email subject',
			},
			{
				id: 'sent_at',
				label: 'Sent At',
				type: 'string',
				description: 'ISO timestamp when email was sent',
			},
			{
				id: 'from_address',
				label: 'From Address',
				type: 'string',
				description: 'Sender email address',
			},
			{
				id: 'template_format',
				label: 'Template Format',
				type: 'string',
				description: 'HTML or plaintext format used',
			},
		];
	},

	slack: nodeData => {
		// Slack nodes produce message metadata for downstream nodes
		return [
			{
				id: 'message_sent',
				label: 'Message Sent',
				type: 'boolean',
				description: 'Whether message was sent successfully',
			},
			{
				id: 'channel_id',
				label: 'Channel ID',
				type: 'string',
				description: 'Slack channel ID where message was sent',
			},
			{
				id: 'message_ts',
				label: 'Message Timestamp',
				type: 'string',
				description: 'Unique message timestamp/ID from Slack',
			},
			{
				id: 'thread_id',
				label: 'Thread ID',
				type: 'string',
				description: 'Thread timestamp if message was sent to a thread',
			},
			{
				id: 'message_url',
				label: 'Message URL',
				type: 'string',
				description: 'Direct URL to the Slack message',
			},
			{
				id: 'bot_id',
				label: 'Bot ID',
				type: 'string',
				description: 'ID of the bot that sent the message',
			},
		];
	},

	hubspot: nodeData => {
		// HubSpot nodes produce dynamic output based on responseFields configuration
		const fields: ContextField[] = [];

		// Add configured response fields if they exist
		if (nodeData.config?.responseFields && Array.isArray(nodeData.config.responseFields)) {
			nodeData.config.responseFields.forEach((field: any) => {
				if (field.key && field.path) {
					fields.push({
						id: field.key,
						label:
							field.key.charAt(0).toUpperCase() +
							field.key.slice(1).replace(/_/g, ' '),
						type: field.type || 'string',
						description: `Extracted from HubSpot response: ${field.path}`,
					});
				}
			});
		}

		// Add default HubSpot fields if no responseFields are configured
		if (fields.length === 0) {
			const apiEndpoint = nodeData.config?.apiEndpoint || '/crm/v3/objects/companies';
			const objectType = apiEndpoint.includes('/companies')
				? 'company'
				: apiEndpoint.includes('/contacts')
				? 'contact'
				: apiEndpoint.includes('/deals')
				? 'deal'
				: 'object';

			fields.push(
				{
					id: 'hubspot_id',
					label: `HubSpot ${objectType.charAt(0).toUpperCase() + objectType.slice(1)} ID`,
					type: 'string',
					description: `Unique HubSpot ${objectType} identifier`,
				},
				{
					id: 'action',
					label: 'Action Performed',
					type: 'string',
					description: 'Whether the record was created or updated',
				},
				{
					id: 'hubspot_data',
					label: `${objectType.charAt(0).toUpperCase() + objectType.slice(1)} Data`,
					type: 'object',
					description: `Complete ${objectType} data from HubSpot`,
				},
				{
					id: 'created_at',
					label: 'Created At',
					type: 'string',
					description: `When the ${objectType} was created`,
				},
				{
					id: 'updated_at',
					label: 'Updated At',
					type: 'string',
					description: `When the ${objectType} was last updated`,
				},
				{
					id: 'properties',
					label: 'Properties',
					type: 'object',
					description: `All ${objectType} properties`,
				}
			);
		}

		// Add the stored result field if storeAs is configured
		if (nodeData.config?.storeAs) {
			fields.push({
				id: nodeData.config.storeAs,
				label: `HubSpot Result (${nodeData.config.storeAs})`,
				type: 'object',
				description: `Complete HubSpot response data stored under ${nodeData.config.storeAs}`,
			});
		}

		return fields;
	},

	// Advanced Node Output Definitions
	loop: nodeData => {
		return [
			{
				id: 'results',
				label: 'Loop Results',
				type: 'array',
				description: 'Array of all loop iteration results',
			},
			{
				id: 'count',
				label: 'Iteration Count',
				type: 'number',
				description: 'Total number of iterations completed',
			},
			{
				id: 'lastItem',
				label: 'Last Item',
				type: 'object',
				description: 'The last item that was processed in the loop',
			},
		];
	},

	wait: nodeData => {
		return [
			{
				id: 'startedAt',
				label: 'Started At',
				type: 'string',
				description: 'ISO timestamp when wait started',
			},
			{
				id: 'resumedAt',
				label: 'Resumed At',
				type: 'string',
				description: 'ISO timestamp when wait ended',
			},
			{
				id: 'actualDuration',
				label: 'Actual Duration',
				type: 'number',
				description: 'Actual wait duration in milliseconds',
			},
		];
	},

	'webhook-listener': nodeData => {
		return [
			{
				id: 'webhook',
				label: 'Webhook Data',
				type: 'object',
				description: 'Complete webhook payload received',
			},
			{
				id: 'receivedAt',
				label: 'Received At',
				type: 'string',
				description: 'ISO timestamp when webhook was received',
			},
			{
				id: 'headers',
				label: 'Request Headers',
				type: 'object',
				description: 'HTTP headers from webhook request',
			},
			{
				id: 'verified',
				label: 'Verified',
				type: 'boolean',
				description: 'Whether webhook signature was verified',
			},
		];
	},

	approval: nodeData => {
		return [
			{
				id: 'status',
				label: 'Approval Status',
				type: 'string',
				description: 'approved or rejected',
			},
			{
				id: 'approvedBy',
				label: 'Approved By',
				type: 'string',
				description: 'Email or ID of person who approved/rejected',
			},
			{
				id: 'at',
				label: 'Decision At',
				type: 'string',
				description: 'ISO timestamp when decision was made',
			},
			{
				id: 'comments',
				label: 'Comments',
				type: 'string',
				description: 'Optional comments from approver',
			},
		];
	},

	logger: nodeData => {
		// Logger nodes don't modify context but provide logging output
		return [
			{
				id: 'logged',
				label: 'Logged',
				type: 'boolean',
				description: 'Whether logging was successful',
			},
			{
				id: 'loggedAt',
				label: 'Logged At',
				type: 'string',
				description: 'ISO timestamp when logging occurred',
			},
			{
				id: 'logLevel',
				label: 'Log Level',
				type: 'string',
				description: 'Level at which data was logged',
			},
		];
	},

	validator: nodeData => {
		return [
			{
				id: 'valid',
				label: 'Validation Result',
				type: 'boolean',
				description: 'Whether all validation rules passed',
			},
			{
				id: 'errors',
				label: 'Validation Errors',
				type: 'array',
				description: 'Array of validation error messages',
			},
			{
				id: 'validatedData',
				label: 'Validated Data',
				type: 'object',
				description: 'The data that was validated',
			},
			{
				id: 'passedRules',
				label: 'Passed Rules',
				type: 'number',
				description: 'Number of validation rules that passed',
			},
			{
				id: 'totalRules',
				label: 'Total Rules',
				type: 'number',
				description: 'Total number of validation rules checked',
			},
		];
	},

	storage: nodeData => {
		return [
			{
				id: 'key',
				label: 'Storage Key',
				type: 'string',
				description: 'Key used to store the data',
			},
			{
				id: 'stored',
				label: 'Successfully Stored',
				type: 'boolean',
				description: 'Whether data was stored successfully',
			},
			{
				id: 'storedAt',
				label: 'Stored At',
				type: 'string',
				description: 'ISO timestamp when data was stored',
			},
			{
				id: 'expiresAt',
				label: 'Expires At',
				type: 'string',
				description: 'ISO timestamp when data expires (if TTL set)',
			},
		];
	},

	'webhook-caller': nodeData => {
		const isAsync = nodeData.config?.async;

		if (isAsync) {
			return [
				{
					id: 'sent',
					label: 'Webhook Sent',
					type: 'boolean',
					description: 'Whether webhook was sent (fire and forget)',
				},
				{
					id: 'sentAt',
					label: 'Sent At',
					type: 'string',
					description: 'ISO timestamp when webhook was sent',
				},
				{
					id: 'url',
					label: 'Target URL',
					type: 'string',
					description: 'URL where webhook was sent',
				},
			];
		} else {
			return [
				{
					id: 'response',
					label: 'Webhook Response',
					type: 'object',
					description: 'Response received from webhook',
				},
				{
					id: 'statusCode',
					label: 'Status Code',
					type: 'number',
					description: 'HTTP status code from response',
				},
				{
					id: 'responseTime',
					label: 'Response Time',
					type: 'number',
					description: 'Response time in milliseconds',
				},
				{
					id: 'sent',
					label: 'Webhook Sent',
					type: 'boolean',
					description: 'Whether webhook was sent successfully',
				},
			];
		}
	},

	switch: nodeData => {
		return [
			{
				id: 'matchedCase',
				label: 'Matched Case',
				type: 'string',
				description: 'Label of the case that matched',
			},
			{
				id: 'caseIndex',
				label: 'Case Index',
				type: 'number',
				description: 'Index of the matched case',
			},
			{
				id: 'usedDefault',
				label: 'Used Default',
				type: 'boolean',
				description: 'Whether default path was used',
			},
			{
				id: 'evaluatedData',
				label: 'Evaluated Data',
				type: 'object',
				description: 'Data used for case evaluation',
			},
		];
	},

	retry: nodeData => {
		return [
			{
				id: 'attempts',
				label: 'Total Attempts',
				type: 'number',
				description: 'Total number of attempts made',
			},
			{
				id: 'succeeded',
				label: 'Finally Succeeded',
				type: 'boolean',
				description: 'Whether operation finally succeeded',
			},
			{
				id: 'lastError',
				label: 'Last Error',
				type: 'string',
				description: 'Last error message if failed',
			},
			{
				id: 'totalRetryTime',
				label: 'Total Retry Time',
				type: 'number',
				description: 'Total time spent retrying in ms',
			},
			{
				id: 'result',
				label: 'Final Result',
				type: 'object',
				description: 'Result from successful attempt or final failure',
			},
		];
	},

	parallel: nodeData => {
		const branches = nodeData.config?.branches || [];
		const branchOutputs = branches.map((branch: any, index: number) => ({
			id: `branch_${index}`,
			label: branch.label || `Branch ${index + 1}`,
			type: 'object' as const,
			description: `Results from ${branch.label || `branch ${index + 1}`}`,
		}));

		return [
			{
				id: 'parallel',
				label: 'All Branch Results',
				type: 'object',
				description: 'Results from all parallel branches',
			},
			{
				id: 'completedBranches',
				label: 'Completed Branches',
				type: 'number',
				description: 'Number of branches that completed',
			},
			{
				id: 'totalBranches',
				label: 'Total Branches',
				type: 'number',
				description: 'Total number of parallel branches',
			},
			{
				id: 'allCompleted',
				label: 'All Completed',
				type: 'boolean',
				description: 'Whether all branches completed successfully',
			},
			...branchOutputs,
		];
	},

	'google-sheets': nodeData => {
		const config = nodeData.config || {};
		const values = config.values || [];
		const rowsCount = values.length;
		const columnsCount = values.length > 0 ? values[0].length : 0;

		return [
			{
				id: 'spreadsheetId',
				label: 'Spreadsheet ID',
				type: 'string',
				description: 'The Google Sheets spreadsheet ID that was written to',
			},
			{
				id: 'range',
				label: 'Range Written',
				type: 'string',
				description: 'The range in the spreadsheet that was written to',
			},
			{
				id: 'rowsWritten',
				label: 'Rows Written',
				type: 'number',
				description: 'Number of rows written to the spreadsheet',
			},
			{
				id: 'columnsWritten',
				label: 'Columns Written',
				type: 'number',
				description: 'Number of columns written to the spreadsheet',
			},
			{
				id: 'appendMode',
				label: 'Append Mode',
				type: 'boolean',
				description: 'Whether data was appended or overwritten',
			},
			{
				id: 'success',
				label: 'Write Success',
				type: 'boolean',
				description: 'Whether the write operation was successful',
			},
			{
				id: 'updatedRange',
				label: 'Updated Range',
				type: 'string',
				description: 'The actual range that was updated (may differ from requested range)',
			},
		];
	},

	stripe: nodeData => {
		const config = nodeData.config || {};
		const operation = config.operation || 'unknown';

		// Return the actual Stripe API response fields based on operation
		switch (operation) {
			case 'create_customer':
				return [
					{
						id: 'id',
						label: 'Customer ID',
						type: 'string',
						description: 'Stripe customer ID (e.g., cus_...)',
					},
					{
						id: 'name',
						label: 'Customer Name',
						type: 'string',
						description: 'Customer full name',
					},
					{
						id: 'email',
						label: 'Customer Email',
						type: 'string',
						description: 'Customer email address',
					},
					{
						id: 'created',
						label: 'Created Timestamp',
						type: 'number',
						description: 'Unix timestamp when customer was created',
					},
					{
						id: 'livemode',
						label: 'Live Mode',
						type: 'boolean',
						description: 'Whether this is a live or test customer',
					},
					{
						id: 'metadata',
						label: 'Metadata',
						type: 'object',
						description: 'Custom metadata associated with the customer',
					},
				];

			case 'create_payment_intent':
			case 'confirm_payment_intent':
				return [
					{
						id: 'id',
						label: 'Payment Intent ID',
						type: 'string',
						description: 'Stripe payment intent ID (e.g., pi_...)',
					},
					{
						id: 'amount',
						label: 'Amount',
						type: 'number',
						description: 'Amount in cents/smallest currency unit',
					},
					{
						id: 'currency',
						label: 'Currency',
						type: 'string',
						description: 'Three-letter ISO currency code',
					},
					{
						id: 'status',
						label: 'Status',
						type: 'string',
						description: 'Current status of the payment intent',
					},
					{
						id: 'client_secret',
						label: 'Client Secret',
						type: 'string',
						description: 'Client secret for frontend confirmation',
					},
					{
						id: 'created',
						label: 'Created Timestamp',
						type: 'number',
						description: 'Unix timestamp when payment intent was created',
					},
				];

			case 'create_subscription':
				return [
					{
						id: 'id',
						label: 'Subscription ID',
						type: 'string',
						description: 'Stripe subscription ID (e.g., sub_...)',
					},
					{
						id: 'status',
						label: 'Status',
						type: 'string',
						description: 'Current subscription status',
					},
					{
						id: 'current_period_start',
						label: 'Period Start',
						type: 'number',
						description: 'Start of current billing period',
					},
					{
						id: 'current_period_end',
						label: 'Period End',
						type: 'number',
						description: 'End of current billing period',
					},
					{
						id: 'created',
						label: 'Created Timestamp',
						type: 'number',
						description: 'Unix timestamp when subscription was created',
					},
				];

			case 'refund_payment':
				return [
					{
						id: 'id',
						label: 'Refund ID',
						type: 'string',
						description: 'Stripe refund ID (e.g., re_...)',
					},
					{
						id: 'amount',
						label: 'Amount',
						type: 'number',
						description: 'Amount refunded in cents',
					},
					{
						id: 'currency',
						label: 'Currency',
						type: 'string',
						description: 'Three-letter ISO currency code',
					},
					{
						id: 'status',
						label: 'Status',
						type: 'string',
						description: 'Current refund status',
					},
					{
						id: 'created',
						label: 'Created Timestamp',
						type: 'number',
						description: 'Unix timestamp when refund was created',
					},
				];

			case 'attach_payment_method':
				return [
					{
						id: 'id',
						label: 'Payment Method ID',
						type: 'string',
						description: 'Stripe payment method ID (e.g., pm_...)',
					},
					{
						id: 'type',
						label: 'Type',
						type: 'string',
						description: 'Payment method type (e.g., card)',
					},
					{
						id: 'card',
						label: 'Card Details',
						type: 'object',
						description: 'Card information including brand, last4, exp_month, exp_year',
					},
					{
						id: 'created',
						label: 'Created Timestamp',
						type: 'number',
						description: 'Unix timestamp when payment method was created',
					},
				];

			case 'create_invoice':
			case 'finalize_invoice':
			case 'send_invoice':
				return [
					{
						id: 'id',
						label: 'Invoice ID',
						type: 'string',
						description: 'Stripe invoice ID (e.g., in_...)',
					},
					{
						id: 'number',
						label: 'Invoice Number',
						type: 'string',
						description: 'Human-readable invoice number',
					},
					{
						id: 'amount_total',
						label: 'Total Amount',
						type: 'number',
						description: 'Total amount for the invoice in cents',
					},
					{
						id: 'currency',
						label: 'Currency',
						type: 'string',
						description: 'Three-letter ISO currency code',
					},
					{
						id: 'status',
						label: 'Status',
						type: 'string',
						description: 'Current invoice status',
					},
					{
						id: 'created',
						label: 'Created Timestamp',
						type: 'number',
						description: 'Unix timestamp when invoice was created',
					},
				];

			default:
				// For unknown operations, return common Stripe object fields
				return [
					{
						id: 'id',
						label: 'Object ID',
						type: 'string',
						description: 'Stripe object ID',
					},
					{
						id: 'created',
						label: 'Created Timestamp',
						type: 'number',
						description: 'Unix timestamp when object was created',
					},
					{
						id: 'livemode',
						label: 'Live Mode',
						type: 'boolean',
						description: 'Whether this is a live or test object',
					},
				];
		}
	},
};

function getFieldType(formFieldType: string): 'string' | 'number' | 'boolean' | 'object' | 'array' {
	switch (formFieldType) {
		case 'number':
			return 'number';
		case 'checkbox':
			return 'boolean';
		case 'select':
		case 'radio':
			return 'string';
		default:
			return 'string';
	}
}
